import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Loader2, AlertTriangle, ChevronDown } from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricDrawer from './MetricDrawer';

/**
 * FunnelTab — Onboarding/activation funnel visualization.
 *
 * Props:
 *   filters: { days, district_id, campus_id }
 */

const FunnelTab = ({ filters = {} }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 30,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const res = await adminAnalytics.getFunnel(params);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load funnel data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading funnel…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
            </div>
        );
    }

    const steps = data?.steps || [];
    const firstCount = steps[0]?.unique_users || 1;

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Activation Funnel</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    User journey from signup to key actions · last {data?.period_days || 30} days
                </p>
            </div>

            {steps.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                    <TrendingUp size={32} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
                    <p style={{ fontSize: 13, margin: 0 }}>
                        No funnel data yet. Events will appear once frontend emits signup and activation events.
                    </p>
                </div>
            ) : (
                <div style={{ maxWidth: 600 }}>
                    {steps.map((step, i) => {
                        const width = firstCount > 0
                            ? Math.max(20, (step.unique_users / firstCount) * 100)
                            : 20;

                        // Dropoff from previous step
                        const prevCount = i > 0 ? steps[i - 1].unique_users : step.unique_users;
                        const dropoff = prevCount > 0 && i > 0
                            ? Math.round((1 - step.unique_users / prevCount) * 100)
                            : 0;

                        return (
                            <div key={step.step}>
                                {/* Step bar */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 12,
                                    marginBottom: 4,
                                }}>
                                    {/* Step number */}
                                    <div style={{
                                        width: 24, height: 24, borderRadius: '50%',
                                        background: step.unique_users > 0 ? '#6366f1' : '#e2e8f0',
                                        color: step.unique_users > 0 ? '#fff' : '#94a3b8',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 11, fontWeight: 700, flexShrink: 0,
                                    }}>
                                        {i + 1}
                                    </div>

                                    {/* Bar */}
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            marginBottom: 4,
                                        }}>
                                            <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                                                {step.label}
                                            </span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{
                                                    fontSize: 14, fontWeight: 700, color: '#111827',
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}>
                                                    {step.unique_users}
                                                </span>
                                                <span style={{
                                                    fontSize: 11, color: '#6b7280',
                                                }}>
                                                    {step.conversion_from_top_pct}%
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{
                                            height: 8, background: '#f1f5f9', borderRadius: 4,
                                            overflow: 'hidden',
                                        }}>
                                            <div style={{
                                                width: `${width}%`, height: '100%',
                                                background: `linear-gradient(90deg, #6366f1, #8b5cf6)`,
                                                borderRadius: 4, transition: 'width 0.5s ease-out',
                                            }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Dropoff indicator */}
                                {i < steps.length - 1 && dropoff > 0 && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 4,
                                        marginLeft: 36, padding: '4px 0 8px',
                                        fontSize: 11, color: '#ef4444',
                                    }}>
                                        <ChevronDown size={12} />
                                        {dropoff}% drop-off
                                    </div>
                                )}
                                {i < steps.length - 1 && dropoff === 0 && (
                                    <div style={{ height: 12 }} />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {data?.note && (
                <p style={{
                    fontSize: 11, color: '#94a3b8', marginTop: 24,
                    borderTop: '1px solid #f1f5f9', paddingTop: 12,
                }}>
                    {data.note}
                </p>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default FunnelTab;
